import ChevronDownIcon from "@redotech/redo-icons/chevron-down.svg";
import {
  StorefrontProductKey,
  StorefrontPublishStatus,
} from "@redotech/redo-team-common/storefront-publish";
import { memo, useEffect, useState } from "react";
import {
  RedoBadge,
  RedoBadgeColor,
  RedoBadgeSize,
} from "./arbiter-components/badge/redo-badge";
import { RedoListItem } from "./arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "./arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Dropdown, DropdownOption } from "./dropdown";
import { Flex } from "./flex";
import * as styles from "./storefront-publish-status-control.module.css";
import { OptionalTooltip } from "./tooltip/optional-tooltip";

/**
 * Display metadata for each {@link StorefrontPublishStatus}. The shared source
 * of truth for the publish-status badge + dropdown across the admin app
 * (general settings, coverage builder, returns settings) and the merchant app
 * (coverage builder). Lives in redo-web so both apps can import it.
 */
export const STOREFRONT_PUBLISH_STATUS_META: Record<
  StorefrontPublishStatus,
  { label: string; shortLabel: string; badgeColor: RedoBadgeColor }
> = {
  [StorefrontPublishStatus.LIVE_SITE]: {
    label: "Published",
    shortLabel: "Published",
    badgeColor: "success",
  },
  [StorefrontPublishStatus.DRAFT_THEMES]: {
    label: "Available in draft themes",
    shortLabel: "Draft themes",
    badgeColor: "warning",
  },
  [StorefrontPublishStatus.DISABLED]: {
    label: "Unpublished",
    shortLabel: "Unpublished",
    badgeColor: "gray",
  },
};

const STATUS_ORDER: StorefrontPublishStatus[] = [
  StorefrontPublishStatus.DISABLED,
  StorefrontPublishStatus.DRAFT_THEMES,
  StorefrontPublishStatus.LIVE_SITE,
];

const toItem = (
  status: StorefrontPublishStatus,
): RedoListItem<StorefrontPublishStatus> => ({
  id: status,
  type: "text",
  text: STOREFRONT_PUBLISH_STATUS_META[status].label,
  value: status,
});

/** A small status pill summarizing a product's current publish status. */
export const StorefrontPublishStatusBadge = memo(
  function StorefrontPublishStatusBadge({
    status,
    size = "sm",
  }: {
    status: StorefrontPublishStatus;
    size?: RedoBadgeSize;
  }) {
    const meta = STOREFRONT_PUBLISH_STATUS_META[status];
    return <RedoBadge color={meta.badgeColor} size={size} text={meta.label} />;
  },
);

/**
 * Three-way publish-status control (Off / Draft themes only / Live) for a
 * single storefront product, rendered as a standard dropdown box. Used on the
 * product settings pages (coverage builder, returns settings) via the
 * configurator.
 */
export const StorefrontPublishStatusControl = memo(
  function StorefrontPublishStatusControl({
    value,
    onChange,
    disabled = false,
    size = "sm",
    productKey,
  }: {
    value: StorefrontPublishStatus;
    onChange: (status: StorefrontPublishStatus) => void;
    disabled?: boolean;
    size?: "xs" | "sm" | "md";
    productKey?: StorefrontProductKey;
  }) {
    return (
      <RedoSingleSelectDropdownInput<StorefrontPublishStatus>
        className="!w-44"
        dataTestId={
          productKey
            ? `storefront-publish-status-${productKey}`
            : "storefront-publish-status"
        }
        disabled={disabled}
        options={STATUS_ORDER.map(toItem)}
        optionSelected={({ item }) => onChange(item.value)}
        selectedItem={toItem(value)}
        size={size}
      />
    );
  },
);

/**
 * Single-pill publish-status control with three presentations:
 * - product off → static gray "Not enabled" pill (no dropdown)
 * - enabled but not editable → static pill colored by status (read-only)
 * - enabled and editable → dropdown trigger colored by status (green when Live)
 *
 * This is the compact control used in the "Storefront publishing" card and the
 * coverage builder.
 */
export const StorefrontPublishStatusPill = memo(
  function StorefrontPublishStatusPill({
    value,
    onChange,
    enabled,
    editable = true,
    size = "sm",
    productKey,
  }: {
    value: StorefrontPublishStatus;
    onChange: (status: StorefrontPublishStatus) => void;
    /** Whether the underlying product is enabled at all. */
    enabled: boolean;
    /** Whether the status can be changed from this control. */
    editable?: boolean;
    size?: RedoBadgeSize;
    productKey?: StorefrontProductKey;
  }) {
    const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null);
    const [open, setOpen] = useState(false);

    // Self-managed outside-click close. Driving the dropdown with the
    // lower-level `Dropdown` + a document mousedown listener (rather than
    // RedoListDropdown's ClickAwayListener) keeps it open through parent
    // re-renders — e.g. the admin card re-rendering when the slow Shopify
    // app-embed lookup resolves — and only closes on a genuine outside click.
    useEffect(() => {
      if (!open) {
        return;
      }
      const onPointerDown = (event: MouseEvent) => {
        const target = event.target as HTMLElement | null;
        if (anchor?.contains(target)) {
          return;
        }
        if (target?.closest("[data-redo-dropdown]")) {
          return;
        }
        setOpen(false);
      };
      document.addEventListener("mousedown", onPointerDown);
      return () => document.removeEventListener("mousedown", onPointerDown);
    }, [open, anchor]);

    const dataTestId = productKey
      ? `storefront-publish-pill-${productKey}`
      : "storefront-publish-pill";

    // Non-editable products are published by enabling/disabling the product
    // itself; the badge explains that on hover instead of offering a dropdown.
    const readOnlyTooltip = editable
      ? undefined
      : "Control publishing by disabling/enabling the product";

    if (!enabled) {
      return (
        <OptionalTooltip
          meetsCondition={!!readOnlyTooltip}
          tooltipProps={{ title: readOnlyTooltip ?? "" }}
        >
          <RedoBadge
            color="gray"
            dataTestId={dataTestId}
            size={size}
            text="Not enabled"
          />
        </OptionalTooltip>
      );
    }

    const meta = STOREFRONT_PUBLISH_STATUS_META[value];

    if (!editable) {
      return (
        <OptionalTooltip
          meetsCondition={!!readOnlyTooltip}
          tooltipProps={{ title: readOnlyTooltip ?? "" }}
        >
          <RedoBadge
            color={meta.badgeColor}
            dataTestId={dataTestId}
            size={size}
            text={meta.label}
          />
        </OptionalTooltip>
      );
    }
    return (
      <>
        <button
          className={styles.pillTrigger}
          data-testid={dataTestId}
          onClick={() => setOpen((o) => !o)}
          ref={setAnchor}
          type="button"
        >
          <RedoBadge
            color={meta.badgeColor}
            iconTrailing={{ type: "icon", Icon: ChevronDownIcon, size }}
            size={size}
            text={meta.label}
          />
        </button>
        <Dropdown anchor={anchor} fitToAnchor={false} open={open}>
          <Flex dir="column" gap="none">
            {STATUS_ORDER.map((status) => (
              <DropdownOption
                action={() => {
                  onChange(status);
                  setOpen(false);
                }}
                key={status}
              >
                {STOREFRONT_PUBLISH_STATUS_META[status].label}
              </DropdownOption>
            ))}
          </Flex>
        </Dropdown>
      </>
    );
  },
);
